/**
 * Keeps the desktop filter sidebar sized so it always ends at the bottom of the
 * viewport — so it is independently scrollable at *any* page scroll position,
 * including the very top.
 *
 * Why this can't be done in CSS alone: the pane is `position: sticky`, and
 * sticky can only hold an element in place — it can never pull it up above its
 * natural flow position. At the top of the page the pane therefore begins below
 * the header / banner / toolbar, so a purely viewport-based height
 * (100vh - sticky offset) overshoots the fold: the lower part of the pane, along
 * with the usable range of its scrollbar, sits below the viewport and can't be
 * reached until the page itself has been scrolled far enough to pin the pane.
 * The correct height depends on the pane's live distance from the viewport top,
 * which is not expressible in CSS.
 *
 * Sets `--facets-available-height` on each pane; collection.css consumes it and
 * falls back to a static calc if this script never runs.
 *
 * Both inputs below are independent of the pane's own height, so there is no
 * measure/resize feedback loop:
 *   - computed `top`  — the resolved sticky offset. Read from CSS rather than
 *                       hardcoded, so it automatically tracks theme.css's four
 *                       header states (default / pinned / hide-nav / scroll-up).
 *   - container top   — the flow position of #FacetFiltersContainer, whose
 *                       height is driven by the product grid beside it.
 */
(() => {
  const SELECTOR = '#FacetFiltersContainer > .sticky-element';
  const BOTTOM_GAP = 20; // px — mirrors the 2rem tail in collection.css
  const MIN_HEIGHT = 240; // px — never collapse to an unusable sliver

  let frame = null;

  const apply = () => {
    frame = null;

    document.querySelectorAll(SELECTOR).forEach((pane) => {
      // Below the xl breakpoint the sidebar is display:none and filtering moves
      // to the drawer. offsetParent is null then; leave the property unset.
      if (pane.offsetParent === null) {
        pane.style.removeProperty('--facets-available-height');
        return;
      }

      const stickyTop = parseFloat(getComputedStyle(pane).top) || 0;
      const containerTop = pane.parentElement.getBoundingClientRect().top;

      // Before the pane pins, its top edge is the container's top edge. Once
      // pinned, it rests at the sticky offset.
      const top = Math.max(stickyTop, containerTop);
      const available = Math.max(MIN_HEIGHT, window.innerHeight - top - BOTTOM_GAP);

      pane.style.setProperty('--facets-available-height', `${Math.round(available)}px`);
    });
  };

  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(apply);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  // facets.js replaces #FacetFiltersContainer's innerHTML on every AJAX filter
  // change, discarding the inline property — afterRenderSection() fires this.
  document.addEventListener('collection:rerendered', schedule);
  // Header height changes across these breakpoints, which moves the sticky top.
  document.addEventListener('matchTablet', schedule);
  document.addEventListener('unmatchTablet', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
